/**
 * 권한 매트릭스 상수 (PRD2 T-0.7, §6).
 * 셀의 결과와 각주는 `docs/20261001-PRD2-사용자및권한공유.md` §6 표와
 * `matrix.test.ts`가 맞춘다. 표에 없는 행동을 여기만 추가하지 않는다.
 */

export const WORKSPACE_ROLES = ["owner", "admin", "editor", "viewer", "guest"] as const;
export type WorkspaceRole = (typeof WORKSPACE_ROLES)[number];

export const PLAN_ROLES = ["owner", "admin", "editor", "contributor", "viewer", "workspaceAdmin"] as const;
export type PlanRole = (typeof PLAN_ROLES)[number];

export const TOKEN_ACTORS = ["self", "workspaceAdmin"] as const;
export type TokenActor = (typeof TOKEN_ACTORS)[number];

export const CAPABILITIES = ["cost.view", "baseline.approve", "export", "share.create"] as const;
export type CapabilityId = (typeof CAPABILITIES)[number];

/** §5.3.3. 역할 기본값. 플래그는 이 역할 밖에서도 켜질 수 있다(baseline.approve는 editor 이상만). */
export const CAPABILITY_DEFAULT_ON: Record<CapabilityId, readonly ("owner" | "admin" | "editor")[]> = {
  "cost.view": ["owner", "admin"],
  "baseline.approve": ["owner", "admin"],
  export: ["owner", "admin", "editor"],
  "share.create": ["owner", "admin"],
};

export type GrantResult = "allow" | "deny" | "conditional";

export type Grant = {
  result: GrantResult;
  /** §6 각주 번호 */
  note?: number;
  /** 각주 12. 이 칸은 해당 능력 플래그가 켜져 있을 때만 허용된다. */
  flag?: CapabilityId;
};

export type ActionRow<R extends string> = {
  id: string;
  /** §6 표의 행동 칸. 굵게·백틱을 뺀 문구 */
  docLabel: string;
  cells: Record<R, Grant>;
};

const A = (note?: number, flag?: CapabilityId): Grant => ({
  result: "allow",
  ...(note ? { note } : {}),
  ...(flag ? { flag } : {}),
});
const N = (note?: number): Grant => ({ result: "deny", ...(note ? { note } : {}) });
const C = (note: number, flag?: CapabilityId): Grant => ({
  result: "conditional",
  note,
  ...(flag ? { flag } : {}),
});

function row<R extends string>(id: string, docLabel: string, cells: Record<R, Grant>): ActionRow<R> {
  return { id, docLabel, cells };
}

export const WORKSPACE_ACTIONS: readonly ActionRow<WorkspaceRole>[] = [
  row("workspace.open", "워크스페이스 열기·멤버 목록 조회", { owner: A(), admin: A(), editor: A(), viewer: A(), guest: N() }),
  row("workspace.settings.update", "일반 설정(이름·아이콘) 변경", { owner: A(), admin: A(), editor: N(), viewer: N(), guest: N() }),
  row("plan.create", "플랜 만들기(로컬 플랜 올리기 포함)", { owner: A(), admin: A(), editor: A(), viewer: N(), guest: N() }),
  row("member.invite", "멤버 초대·역할 변경(admin 이하)", { owner: A(), admin: A(1), editor: N(), viewer: N(), guest: N() }),
  row("member.suspendRemove", "멤버 중지·제거", { owner: A(), admin: A(2), editor: N(), viewer: N(), guest: N() }),
  row("inviteLink.manage", "공용 초대 링크 만들기·회전·철회", { owner: A(), admin: A(), editor: N(), viewer: N(), guest: N() }),
  row("accessRequest.review", "접근 요청 승인·거절·차단", { owner: A(), admin: A(), editor: N(), viewer: N(), guest: N() }),
  row("policy.security.update", "보안 정책 변경(허용 도메인·토큰·MFA 안내)", { owner: A(), admin: A(3), editor: N(), viewer: N(), guest: N() }),
  row("policy.share.update", "공유 정책 변경(외부 공유 허용·기본 만료·비밀번호 필수)", { owner: A(), admin: A(3), editor: N(), viewer: N(), guest: N() }),
  row("audit.read", "감사 로그 조회·내보내기", { owner: A(), admin: A(), editor: N(), viewer: N(), guest: N() }),
  row("plan.meta.readAll", "모든 플랜의 메타 조회(이름·오너·멤버·상태·크기)", { owner: A(), admin: A(), editor: N(), viewer: N(), guest: N() }),
  row("plan.content.readWithoutMembership", "플랜 콘텐츠 읽기(플랜 멤버가 아닐 때)", { owner: N(4), admin: N(4), editor: N(), viewer: N(), guest: N() }),
  row("resourcePool.manage", "인력 풀(자원·팀·단가) 관리", { owner: A(), admin: A(), editor: C(5), viewer: N(), guest: N() }),
  row("share.manageAll", "전체 공유 링크 목록·철회·일시 중지", { owner: A(), admin: A(), editor: N(), viewer: N(), guest: N() }),
  row("token.manageAll", "전체 개인 액세스 토큰 목록·폐기", { owner: A(), admin: A(), editor: N(), viewer: N(), guest: N() }),
  row("workspace.archive", "워크스페이스 아카이브", { owner: A(6), admin: N(), editor: N(), viewer: N(), guest: N() }),
  row("workspace.trash", "워크스페이스 삭제(휴지통)", { owner: A(6), admin: N(), editor: N(), viewer: N(), guest: N() }),
  row("workspace.transferOwnership", "소유권 이전", { owner: A(), admin: N(), editor: N(), viewer: N(), guest: N() }),
  row("workspace.leave", "워크스페이스 나가기", { owner: A(7), admin: A(), editor: A(), viewer: A(), guest: A() }),
];

export const PLAN_ACTIONS: readonly ActionRow<PlanRole>[] = [
  row("plan.meta.read", "플랜 존재·메타 조회", { owner: A(), admin: A(), editor: A(), contributor: A(), viewer: A(), workspaceAdmin: A() }),
  row("plan.schedule.read", "일정·현황·캘린더 읽기", { owner: A(), admin: A(), editor: A(), contributor: A(), viewer: A(), workspaceAdmin: N(4) }),
  row("plan.wbs.edit", "WBS 구조 편집(추가·삭제·이동·레벨·선행)", { owner: A(), admin: A(), editor: A(), contributor: N(), viewer: N(), workspaceAdmin: N() }),
  row("plan.schedule.edit", "일정 입력(기간·제약·캘린더) 편집", { owner: A(), admin: A(), editor: A(), contributor: N(), viewer: N(), workspaceAdmin: N() }),
  row("plan.progress.editOwn", "본인 배정 작업의 진척·실적 입력", { owner: A(), admin: A(), editor: A(), contributor: A(), viewer: N(), workspaceAdmin: N() }),
  row("plan.progress.editOthers", "타인 배정 작업의 진척·실적 입력", { owner: A(), admin: A(), editor: A(), contributor: N(), viewer: N(), workspaceAdmin: N() }),
  row("plan.task.edit", "실행 Task 만들기·편집", { owner: A(), admin: A(), editor: A(), contributor: C(8), viewer: N(), workspaceAdmin: N() }),
  row("plan.issue.edit", "이슈·리스크·변경 요청 작성·편집", { owner: A(), admin: A(), editor: A(), contributor: C(8), viewer: N(), workspaceAdmin: N() }),
  row("plan.doc.read", "이 플랜 공개 문서를 읽기", { owner: A(), admin: A(), editor: A(), contributor: A(), viewer: A(), workspaceAdmin: N(4) }),
  row("plan.doc.edit", "이 플랜 공개 문서를 만들고 본문·제목·종류·연결·첨부를 고치기", { owner: A(), admin: A(), editor: A(), contributor: C(8), viewer: N(), workspaceAdmin: N() }),
  row("plan.doc.delete", "편집할 수 있는 문서를 휴지통으로", { owner: A(), admin: A(), editor: A(), contributor: C(8), viewer: N(), workspaceAdmin: N() }),
  row("plan.doc.restore", "문서 휴지통 복원", { owner: C(18), admin: C(18), editor: C(18), contributor: C(18), viewer: N(), workspaceAdmin: N() }),
  row("plan.record.draft", "정책·결정 작성·편집(제안까지)", { owner: A(), admin: A(), editor: A(), contributor: N(), viewer: N(), workspaceAdmin: N() }),
  row("plan.record.confirm", "정책·결정 확정(시행 중) 전환, 개정 확정", { owner: A(), admin: A(), editor: C(9), contributor: N(), viewer: N(), workspaceAdmin: N() }),
  row("plan.comment.write", "댓글 작성, 본인 댓글 수정·삭제", { owner: A(), admin: A(), editor: A(), contributor: A(), viewer: C(10), workspaceAdmin: N() }),
  row("plan.comment.deleteOthers", "타인 댓글 삭제", { owner: A(), admin: A(), editor: N(), contributor: N(), viewer: N(), workspaceAdmin: N() }),
  row("plan.attachment.upload", "첨부 올리기", { owner: A(), admin: A(), editor: A(), contributor: C(8), viewer: N(), workspaceAdmin: N() }),
  row("plan.attachment.download", "첨부 내려받기", { owner: A(), admin: A(), editor: A(), contributor: A(), viewer: C(11), workspaceAdmin: N() }),
  row("plan.baseline.create", "기준선 만들기", { owner: A(), admin: A(), editor: A(), contributor: N(), viewer: N(), workspaceAdmin: N() }),
  row("plan.baseline.approve", "기준선 승인·교체·삭제", { owner: A(), admin: A(), editor: C(12, "baseline.approve"), contributor: N(), viewer: N(), workspaceAdmin: N() }),
  row("plan.cost.read", "금액·단가·예산 조회", { owner: A(), admin: A(), editor: C(12, "cost.view"), contributor: C(12, "cost.view"), viewer: C(12, "cost.view"), workspaceAdmin: N() }),
  row("plan.cost.edit", "예산·단가 편집", { owner: A(), admin: A(), editor: N(), contributor: N(), viewer: N(), workspaceAdmin: N() }),
  row("plan.resource.assign", "리소스(인력) 배정 편집", { owner: A(), admin: A(), editor: A(), contributor: N(), viewer: N(), workspaceAdmin: N() }),
  row("plan.export", "Excel·JSON·화면 저장 내보내기", { owner: A(), admin: A(), editor: A(), contributor: C(12, "export"), viewer: C(12, "export"), workspaceAdmin: N() }),
  row("plan.share.create", "공유 링크 만들기(자기 것 수정·철회 포함)", { owner: A(), admin: A(), editor: C(12, "share.create"), contributor: C(12, "share.create"), viewer: C(12, "share.create"), workspaceAdmin: N() }),
  row("plan.share.manageOthers", "타인 공유 링크 수정·철회·일시 중지", { owner: A(), admin: A(), editor: N(), contributor: N(), viewer: N(), workspaceAdmin: A(13) }),
  row("plan.member.manage", "멤버 초대·역할·플래그 변경", { owner: A(), admin: A(14), editor: N(), contributor: N(), viewer: N(), workspaceAdmin: N() }),
  row("plan.settings.update", "플랜 설정(이름·기간·가시성·알림)", { owner: A(), admin: A(), editor: N(), contributor: N(), viewer: N(), workspaceAdmin: N() }),
  row("plan.audit.read", "플랜 감사 조회", { owner: A(), admin: A(), editor: N(), contributor: N(), viewer: N(), workspaceAdmin: A(13) }),
  row("plan.archive", "플랜 아카이브", { owner: A(), admin: N(), editor: N(), contributor: N(), viewer: N(), workspaceAdmin: A() }),
  row("plan.unarchive", "아카이브 복구", { owner: A(), admin: N(), editor: N(), contributor: N(), viewer: N(), workspaceAdmin: A() }),
  row("plan.trash", "플랜 삭제(휴지통)", { owner: A(), admin: N(), editor: N(), contributor: N(), viewer: N(), workspaceAdmin: N(15) }),
  row("plan.restore", "휴지통 복구", { owner: A(), admin: N(), editor: N(), contributor: N(), viewer: N(), workspaceAdmin: A() }),
  row("plan.transferOwnership", "플랜 소유권 이전", { owner: A(), admin: N(), editor: N(), contributor: N(), viewer: N(), workspaceAdmin: A(16) }),
  row("plan.leave", "플랜 나가기", { owner: N(17), admin: A(), editor: A(), contributor: A(), viewer: A(), workspaceAdmin: N() }),
  row("plan.export.localJson", "로컬 사본으로 내보내기(JSON)", { owner: A(), admin: A(), editor: A(), contributor: C(12, "export"), viewer: C(12, "export"), workspaceAdmin: N() }),
];

export const TOKEN_ACTIONS: readonly ActionRow<TokenActor>[] = [
  row("token.own.manage", "내 PAT 발급·조회(접두어·범위·만료·마지막 사용)·폐기", { self: A(), workspaceAdmin: N() }),
  row("token.others.manage", "다른 사람의 PAT 조회(접두어·범위·만료·마지막 사용)·폐기", { self: N(), workspaceAdmin: A() }),
  row("session.own.manage", "내 기기 세션(웹·데스크톱) 조회·원격 로그아웃", { self: A(), workspaceAdmin: N() }),
  row("session.others.revoke", "다른 사람의 기기 세션 원격 로그아웃", { self: N(), workspaceAdmin: A() }),
  row("auth.provider.manage", "내 로그인 제공자 연결·해제", { self: A(), workspaceAdmin: N() }),
];

export function workspaceGrant(actionId: string, role: WorkspaceRole): Grant {
  return grantOf(WORKSPACE_ACTIONS, actionId, role);
}

export function planGrant(actionId: string, role: PlanRole): Grant {
  return grantOf(PLAN_ACTIONS, actionId, role);
}

function grantOf<R extends string>(rows: readonly ActionRow<R>[], actionId: string, role: R): Grant {
  const found = rows.find((item) => item.id === actionId);
  if (!found) throw new Error(`없는 행동: ${actionId}`);
  return found.cells[role];
}
